import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import cn from "classnames";
import type React from "react";
import {
  isValidElement as isReactNode,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { commonMessages } from "../../utils/messages";

import "./PageHeader.scss";
import { Image } from "@evidenceb/athena-common/modules/Assets";
import classNames from "classnames";
import useAssetsDetails from "../../hooks/useAssetsDetails";

export type Props = PropsWithChildren<{
  title: {
    content: string | ReactNode;
    size?: "default" | "large" | "custom";
    className?: string;
  };
  children?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
  /**
   * URL the backlink should point to. Defaults to using history.goBack
   */
  backTo?: string;
  /**
   * URL the backlink points to if history.goBack has no effect because there
   * is no history
   */
  fallbackBackTo?: string;
  /**
   * Remove chevron icon and backTo link
   * @default false
   */
  noBackBtn?: boolean;
  className?: string;
  background?: string;
}>;

const PageHeader = ({
  title,
  children,
  className,
  backTo,
  fallbackBackTo,
  noBackBtn,
  background,
}: Props) => {
  const history = useHistory();
  const intl = useIntl();
  const { isMobile } = useBreakpoints();

  const ref = useRef<HTMLDivElement>(null);
  const assetsDetails = useAssetsDetails();

  const [isAtTop, setIsAtTop] = useState(true);
  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY === 0);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <header
      ref={ref}
      className={cn("page-header", className, {
        "--is-scrolling": !isAtTop,
      })}
    >
      <div className="page-header__content">
        <div
          className={classNames(
            "page-header__title",
            title.size !== "custom"
              ? `--${title.size ? title.size : "default"}`
              : undefined,
            `${title.className}`,
          )}
        >
          {!noBackBtn && (
            <button
              onClick={() => {
                if (backTo) history.push(backTo);
                else if (history.length <= 1 && fallbackBackTo)
                  history.push(fallbackBackTo);
                else history.goBack();
              }}
            >
              <Icon
                path="chevron_left"
                size={isMobile ? "medium" : "large"}
                color="var(--text-main-default)"
              />
              <VisuallyHidden as="span">
                {intl.formatMessage(commonMessages.goBack)}
              </VisuallyHidden>
            </button>
          )}
          {isReactNode(title.content) ? (
            title.content
          ) : (
            <h1>{title.content}</h1>
          )}
        </div>
        {isMobile && children && (
          <div className="page-header__children">{children}</div>
        )}
      </div>

      {!isMobile && children}
      {background && (
        <div className="page-header__background">
          <Image
            assetsDetails={assetsDetails}
            src={background}
            aria-hidden={true}
            alt=""
          />
        </div>
      )}
    </header>
  );
};
export default PageHeader;
